import { useState } from "react";
import { Check, X, Lock, PaperPlaneTilt, Rocket, ArrowUUpLeft, PenNib } from "@phosphor-icons/react";

const SIGNOFF = ["lead", "project_manager", "admin"];

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid={testid}>
      <div className="w-[420px] bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4">
        <div className="text-sm text-[var(--c-text)] mb-3">{title}</div>
        {children}
        <div className="flex justify-end mt-3">
          <button onClick={onClose} className="h-7 px-3 text-[11px] text-[var(--c-muted)] hover:text-[var(--c-text)]" data-testid={`${testid}-cancel`}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function ApprovalBar({ drawing, me, checkers, onAction, onNewRevision, busy }) {
  const [modal, setModal] = useState(null); // submit | return | approveReturn | release | revise
  const [comment, setComment] = useState("");
  const [checkerId, setCheckerId] = useState("");
  const [confirmNo, setConfirmNo] = useState("");
  const [revDesc, setRevDesc] = useState("");
  const [err, setErr] = useState("");

  if (!drawing || !me) return null;
  const st = drawing.status;
  const role = me.role;
  const canSignoff = SIGNOFF.includes(role);
  const isLastEditor = drawing.updated_by?.id === me.id;
  const checkedBy = drawing.signatures?.checked?.by_id;
  const locked = st === "approved" || st === "released";

  const act = async (action, extra = {}) => {
    setErr("");
    try {
      await onAction(action, extra);
      setModal(null); setComment(""); setConfirmNo(""); setRevDesc("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Action failed");
    }
  };

  const B = ({ id, onClick, icon: Icon, children, color = "var(--c-accent)", disabled }) => (
    <button data-testid={id} onClick={onClick} disabled={disabled || busy}
      className="h-7 px-2.5 rounded-sm text-[11px] flex items-center gap-1.5 disabled:opacity-30 transition-colors"
      style={{ background: `color-mix(in srgb, ${color} 13%, transparent)`, color, border: `1px solid color-mix(in srgb, ${color} 27%, transparent)` }}>
      <Icon size={12} /> {children}
    </button>
  );

  return (
    <>
      <div className="flex items-center gap-2 px-3 h-9 bg-[var(--c-surface)]/60 border-b border-[var(--c-border)] shrink-0" data-testid="approval-bar">
        <span className="font-mono text-[10px] uppercase tracking-wider" data-testid="approval-status"
          style={{ color: { draft: "var(--c-muted)", checked: "var(--c-accent)", approved: "var(--c-green)", released: "var(--c-amber)" }[st] }}>
          {st}{drawing.substatus === "submitted_for_check" ? ` · submitted to ${drawing.checker_name || "checker"}` : ""}
        </span>

        {locked && (
          <span className="text-[10px] text-[var(--c-accent)]" data-testid="locked-banner">
            🔒 This drawing is {st}. Edits will create a new revision.
          </span>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          {st === "draft" && (role === "engineer" || role === "lead" || role === "admin") && (
            <B id="submit-check-btn" icon={PaperPlaneTilt} onClick={() => setModal("submit")}>Submit for Check</B>
          )}
          {st === "draft" && canSignoff && !isLastEditor && (
            <B id="mark-checked-btn" icon={Check} color="var(--c-accent)" onClick={() => act("check")}>Mark as Checked</B>
          )}
          {st === "draft" && canSignoff && isLastEditor && (
            <span className="text-[10px] text-[var(--c-muted)]" data-testid="self-check-note" title="You cannot check your own work">Awaiting another reviewer</span>
          )}
          {(st === "draft" || st === "checked") && canSignoff && (
            <B id="return-corrections-btn" icon={ArrowUUpLeft} color="var(--c-amber)" onClick={() => setModal("return")}>Return</B>
          )}
          {st === "checked" && canSignoff && checkedBy !== me.id && (
            <B id="approve-btn" icon={Check} color="var(--c-green)" onClick={() => act("approve")}>Approve</B>
          )}
          {st === "checked" && canSignoff && checkedBy === me.id && (
            <span className="text-[10px] text-[var(--c-muted)]" data-testid="self-approve-note">A different authorized person must approve</span>
          )}
          {st === "approved" && (role === "lead" || role === "admin") && (
            <B id="release-btn" icon={Rocket} color="var(--c-amber)" onClick={() => setModal("release")}>Release</B>
          )}
          {locked && (
            <B id="new-revision-btn" icon={PenNib} color="var(--c-accent)" onClick={() => setModal("revise")}>Create Revision</B>
          )}
        </div>
      </div>

      {/* signature cards */}
      {modal === null && (
        <div className="hidden" data-testid="signature-data"
          data-drawn={drawing.signatures?.drawn?.by || ""}
          data-checked={drawing.signatures?.checked?.by || ""}
          data-approved={drawing.signatures?.approved?.by || ""}
          data-released={drawing.signatures?.released?.by || ""} />
      )}

      {modal === "submit" && (
        <Modal title="Submit for Check" onClose={() => setModal(null)} testid="submit-check-modal">
          <label className="text-[10px] text-[var(--c-muted)] block mb-1">Checker (Lead / PM / Admin)</label>
          <select data-testid="checker-select" value={checkerId} onChange={(e) => setCheckerId(e.target.value)}
            className="w-full h-8 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-xs rounded-sm px-2">
            <option value="">Select checker…</option>
            {checkers
              .filter((u, i, arr) => (u.id || u.user_id) !== me.id
                && (u.email || "").toLowerCase() !== (me.email || "").toLowerCase()
                && arr.findIndex((x) => (x.id || x.user_id) === (u.id || u.user_id)) === i
                && arr.findIndex((x) => (x.email || "").toLowerCase() === (u.email || "").toLowerCase()) === i)
              .map((u) => <option key={u.id || u.user_id} value={u.id || u.user_id}>{u.name} ({u.role})</option>)}
          </select>
          {err && <div className="text-[11px] text-[var(--c-red)] mt-2" data-testid="modal-error">{err}</div>}
          <button data-testid="submit-check-confirm" onClick={() => act("submit_check", { checker_id: checkerId })}
            disabled={!checkerId}
            className="mt-3 w-full h-8 bg-[var(--c-accent)] text-white text-xs rounded-sm disabled:opacity-40">Submit</button>
        </Modal>
      )}

      {modal === "return" && (
        <Modal title={st === "checked" ? "Return to Draft" : "Return for Corrections"} onClose={() => setModal(null)} testid="return-modal">
          <label className="text-[10px] text-[var(--c-muted)] block mb-1">Comment (required — what must be fixed)</label>
          <textarea data-testid="return-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
            className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-xs rounded-sm p-2" />
          {err && <div className="text-[11px] text-[var(--c-red)] mt-2" data-testid="modal-error">{err}</div>}
          <button data-testid="return-confirm" disabled={comment.trim().length < 5}
            onClick={() => act(st === "checked" ? "return_draft" : "return_corrections", { comment })}
            className="mt-3 w-full h-8 bg-[var(--c-amber)] text-white text-xs rounded-sm disabled:opacity-40">Return</button>
        </Modal>
      )}

      {modal === "release" && (
        <Modal title="Release for Manufacturing" onClose={() => setModal(null)} testid="release-modal">
          <div className="text-[11px] text-[var(--c-amber)] mb-2">
            Releasing authorizes this drawing for manufacturing. This action is logged and audited.
          </div>
          <label className="text-[10px] text-[var(--c-muted)] block mb-1">Type the drawing number to confirm: <span className="font-mono text-[var(--c-text)]">{drawing.drawing_no}</span></label>
          <input data-testid="release-confirm-input" value={confirmNo} onChange={(e) => setConfirmNo(e.target.value)}
            className="w-full h-8 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-xs font-mono rounded-sm px-2" />
          {err && <div className="text-[11px] text-[var(--c-red)] mt-2" data-testid="modal-error">{err}</div>}
          <button data-testid="release-confirm-btn" disabled={confirmNo !== drawing.drawing_no}
            onClick={() => act("release", { confirm_no: confirmNo })}
            className="mt-3 w-full h-8 bg-[var(--c-amber)] text-white text-xs rounded-sm disabled:opacity-40">Release</button>
        </Modal>
      )}

      {modal === "revise" && (
        <Modal title={`Create new revision of ${drawing.drawing_no} (Rev ${drawing.rev_label || "0"})`} onClose={() => setModal(null)} testid="revision-modal">
          {st === "released" && (
            <div className="text-[11px] text-[var(--c-amber)] mb-2">This drawing has been released for manufacturing. Creating a revision will trigger a change notice.</div>
          )}
          <label className="text-[10px] text-[var(--c-muted)] block mb-1">Describe what changed (required)</label>
          <textarea data-testid="rev-desc-input" value={revDesc} onChange={(e) => setRevDesc(e.target.value)} rows={3}
            placeholder="e.g., Updated bushing positions per client comment"
            className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-xs rounded-sm p-2" />
          {err && <div className="text-[11px] text-[var(--c-red)] mt-2" data-testid="modal-error">{err}</div>}
          <button data-testid="rev-create-btn" disabled={revDesc.trim().length < 3}
            onClick={async () => { setErr(""); try { await onNewRevision(revDesc.trim()); setModal(null); setRevDesc(""); } catch (e) { setErr(e?.response?.data?.detail || e.message); } }}
            className="mt-3 w-full h-8 bg-[var(--c-accent)] text-white text-xs rounded-sm disabled:opacity-40">Create Revision</button>
        </Modal>
      )}
    </>
  );
}
